import { Button, Select, SelectOption } from "@rio-cloud/rio-uikit";
import OverlayTrigger from "@rio-cloud/rio-uikit/OverlayTrigger";
import Tooltip from "@rio-cloud/rio-uikit/Tooltip";
import { NameInputChange } from "./form";
import SimpleTooltip from "@rio-cloud/rio-uikit/SimpleTooltip";
import Spinner from "@rio-cloud/rio-uikit/Spinner";
import { CSSProperties, ReactNode, UIEvent, useCallback, useEffect, useRef } from "react";
import { useT } from "@hooks";

interface InactiveSelectOption extends SelectOption {
  inactive?: boolean;
  rawName?: string;
}

const FEMININE_ENTITIES = new Set(["filial", "categoria", "subcategoria"]);

const PARENTHESES_STOPS = new Set([")", "\r", "\n", "\u2028", "\u2029"]);

export const dividirPorParenteses = (texto: string): string[] => {
  const partes: string[] = [];
  let inicio = 0;
  let i = 0;

  while (i < texto.length) {
    if (texto[i] !== "(") {
      i += 1;
      continue;
    }

    let fim = i + 1;
    while (fim < texto.length && !PARENTHESES_STOPS.has(texto[fim])) {
      fim += 1;
    }

    if (texto[fim] === ")") {
      partes.push(texto.slice(inicio, i), texto.slice(i, fim + 1));
      inicio = fim + 1;
    }

    i = fim + 1;
  }

  partes.push(texto.slice(inicio));
  return partes;
};

const rotuloComoTexto = (label: SelectOption["label"]): string => (typeof label === "string" ? label : "");

const dividirPorParentesesComChave = (texto: string) => {
  let posicao = 0;

  return dividirPorParenteses(texto).map((parte) => {
    const chave = `${posicao}-${parte}`;
    posicao += parte.length;
    return { chave, parte };
  });
};

const RESET_BOTAO: CSSProperties = { background: "none", border: "none", padding: 0 };

const getGenderedText = (entity: string, t: (id: string, values?: Record<string, string | number>) => string) => {
  const fem = FEMININE_ENTITIES.has(entity);
  const gender = fem ? "f" : "m";
  return {
    title: t(`select.inactive.title.${gender}`, { entity }),
    body: t(`select.inactive.body.${gender}`, { entity }),
    instruction: t(`select.inactive.instruction.${gender}`, { entity }),
    link: t("select.inactive.link", { entity }),
  };
};

const DROPDOWN_STYLE_ID = "inactive-select-dropdown-style";
const injectDropdownStyles = () => {
  if (document.getElementById(DROPDOWN_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = DROPDOWN_STYLE_ID;
  style.textContent = [`.selected-option-dropdown-item { display: flex; width: 100%; flex: 1; }`].join("\n");
  document.head.appendChild(style);
};

export const PINNED_FIRST_OPTION_CLASS = "select-dropdown-pinned-first";
const PINNED_FIRST_OPTION_STYLE_ID = "pinned-first-option-dropdown-style";
const injectPinnedFirstOptionStyles = () => {
  if (document.getElementById(PINNED_FIRST_OPTION_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = PINNED_FIRST_OPTION_STYLE_ID;
  style.textContent = `.${PINNED_FIRST_OPTION_CLASS} > li:first-child { position: sticky; top: 0; z-index: 1; background-color: inherit; }`;
  document.head.appendChild(style);
};

interface Props {
  errorMessage?: string;
  label: string;
  options: InactiveSelectOption[];
  newOption?: SelectOption;
  onCreateNewOption: (name: NameInputChange) => void;
  inputChange: (name: NameInputChange, value?: string) => void;
  value: string;
  name: NameInputChange;
  onRight?: () => void;
  iconRight?: string;
  tooltip?: ReactNode;
  disabled?: boolean;
  disabledRight?: boolean;
  useClear?: boolean;
  useFilter?: boolean;
  onInactiveSelect?: (item: InactiveSelectOption) => void;
  inactiveEntityLabel?: string;
  onFilterChange?: (value: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  pinNewOption?: boolean;
}

const FILTER_INPUT_CLASS = "select-filter-input";
const OPEN_DROPDOWN_MENU_SELECTOR = ".select.open .dropdown-menu";
const LOAD_MORE_THRESHOLD_PX = 64;
export const LOADING_MORE_OPTION_ID = "__loading-more__";

const isNearBottom = (element: HTMLElement) =>
  element.scrollHeight - element.scrollTop - element.clientHeight <= LOAD_MORE_THRESHOLD_PX;

const InputSelect = ({
  errorMessage,
  label,
  newOption,
  options,
  inputChange,
  onCreateNewOption,
  value,
  name,
  onRight,
  iconRight,
  disabled,
  disabledRight,
  tooltip,
  useClear,
  useFilter = true,
  onInactiveSelect,
  inactiveEntityLabel,
  onFilterChange,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  pinNewOption = false,
}: Props) => {
  const t = useT();
  const isNewOptionPinned = pinNewOption && !!newOption;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canLoadMore = !!onLoadMore && hasMore && !isLoadingMore;

  const loadMoreIfNeeded = useCallback(() => {
    if (!canLoadMore) return;

    const menu = wrapperRef.current?.querySelector<HTMLElement>(OPEN_DROPDOWN_MENU_SELECTOR);

    if (menu && isNearBottom(menu)) onLoadMore?.();
  }, [canLoadMore, onLoadMore]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(loadMoreIfNeeded);

    return () => window.cancelAnimationFrame(frame);
  }, [options.length, loadMoreIfNeeded]);

  useEffect(() => {
    if (options.some((o) => o.inactive)) injectDropdownStyles();
  }, [options]);

  useEffect(() => {
    if (isNewOptionPinned) injectPinnedFirstOptionStyles();
  }, [isNewOptionPinned]);

  const entityLabel = inactiveEntityLabel || "item";
  const txt = getGenderedText(entityLabel, t);
  const selectedOption = value ? options.find((o) => o.id === value) : undefined;
  const selectedInactiveOption = selectedOption?.inactive ? selectedOption : null;

  const renderInactiveTooltip = (opt: InactiveSelectOption) => (
    <Tooltip id={`inactive-tooltip-${name}-${opt.id}`} width={400} textAlignment={Tooltip.TEXT_ALIGNMENT_LEFT}>
      <div className="padding-5">
        <p className="margin-0 margin-bottom-5 text-medium text-size-14">{txt.title}</p>
        <p className="margin-0 margin-bottom-5 text-size-14 line-height-125rel">{txt.body}</p>
        <p className="margin-0 margin-bottom-5 text-size-14 line-height-125rel">{txt.instruction}</p>
        <button
          type="button"
          className="text-size-14 text-color-primary cursor-pointer text-italic"
          style={{ ...RESET_BOTAO, textDecoration: "underline" }}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              e.preventDefault();
              onInactiveSelect?.(opt);
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onInactiveSelect?.(opt);
          }}
        >
          {txt.link}
        </button>
      </div>
    </Tooltip>
  );

  const renderSelect = () => {
    const styledOptions: SelectOption[] = options.map((opt) => {
      if (opt.inactive) {
        if (disabled) {
          return {
            ...opt,
            label: (<span className="text-color-danger text-italic">{opt.label}</span>) as unknown as string,
          };
        }
        return {
          ...opt,
          label: (
            <span className="display-flex align-items-center width-100pct">
              <span className="text-color-danger text-italic flex-1-1" style={{ minWidth: 0 }}>
                {opt.label}
              </span>
              <OverlayTrigger placement="top-end" trigger="click" overlay={renderInactiveTooltip(opt)} rootClose>
                <button
                  type="button"
                  aria-label={txt.link}
                  className="rioglyph rioglyph-info-sign text-color-primary text-size-16 margin-left-auto padding-left-10 cursor-pointer"
                  style={{ ...RESET_BOTAO, flexShrink: 0 }}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      e.preventDefault();
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                />
              </OverlayTrigger>
            </span>
          ) as unknown as string,
        };
      }
      return {
        ...opt,
        label: dividirPorParentesesComChave(rotuloComoTexto(opt.label)).map(({ chave, parte }) =>
          parte.startsWith("(") ? (
            <span key={chave} className="text-size-10 text-light text-gray text-italic">
              {parte}
            </span>
          ) : (
            parte
          )
        ),
      };
    });

    const pagedOptions =
      onLoadMore && isLoadingMore
        ? [
            ...styledOptions,
            {
              id: LOADING_MORE_OPTION_ID,
              disabled: true,
              label: (
                <span className="display-flex align-items-center gap-5 text-color-gray">
                  <Spinner />
                  {t("common.label.loading")}
                </span>
              ) as unknown as string,
            },
          ]
        : styledOptions;

    const inputOptions = newOption
      ? isNewOptionPinned
        ? [newOption, ...pagedOptions]
        : [...pagedOptions, newOption]
      : pagedOptions;

    return (
      <Select
        id={name}
        disabled={disabled}
        placeholder={t("common.select")}
        options={inputOptions}
        useClear={useClear}
        useFilter={useFilter}
        onChange={(item) => {
          if (item?.id === "other") {
            onCreateNewOption(name);
            return;
          }
          const original = options.find((o) => o.id === item?.id);
          if (original?.inactive && onInactiveSelect) {
            onInactiveSelect(original);
            return;
          }
          inputChange(name, item?.id as string);
        }}
        value={[value]}
        className="rounded-small flex-1-1"
        dropdownClassName={isNewOptionPinned ? PINNED_FIRST_OPTION_CLASS : undefined}
        errorMessage={errorMessage}
      />
    );
  };

  const renderAcessorioDireita = () => {
    if (disabled && selectedInactiveOption && onInactiveSelect) {
      return (
        <OverlayTrigger
          placement="top-end"
          trigger="hover"
          overlay={
            <Tooltip id={`inactive-edit-${name}`}>
              <span className="text-italic">{txt.link}</span>
            </Tooltip>
          }
        >
          <button
            type="button"
            aria-label={txt.link}
            className="rioglyph rioglyph-info-sign text-color-primary text-size-20 cursor-pointer"
            style={{ ...RESET_BOTAO, flexShrink: 0 }}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onInactiveSelect(selectedInactiveOption);
              }
            }}
            onClick={() => onInactiveSelect(selectedInactiveOption)}
          />
        </OverlayTrigger>
      );
    }

    if (iconRight) {
      return <Button disabled={disabledRight} onClick={onRight} iconName={iconRight} iconOnly />;
    }

    if (tooltip) {
      return (
        <div className="display-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}>
          <SimpleTooltip content={tooltip} placement="top-end">
            <span className="rioglyph rioglyph-info-sign text-color-primary text-size-18 cursor-pointer" />
          </SimpleTooltip>
        </div>
      );
    }

    return null;
  };

  const capturarFiltro = (evento: React.FormEvent<HTMLDivElement>) => {
    if (!onFilterChange) return;

    const alvo = evento.target as HTMLInputElement;

    if (!alvo?.classList?.contains(FILTER_INPUT_CLASS)) return;

    onFilterChange(alvo.value);
  };

  const capturarRolagem = (evento: UIEvent<HTMLDivElement>) => {
    if (!canLoadMore) return;

    const alvo = evento.target as HTMLElement;

    if (!alvo?.matches?.(OPEN_DROPDOWN_MENU_SELECTOR)) return;

    if (isNearBottom(alvo)) onLoadMore?.();
  };

  const verificarAoAbrir = () => {
    if (canLoadMore) window.requestAnimationFrame(loadMoreIfNeeded);
  };

  return (
    <div className={`form-group ${errorMessage ? "has-error" : ""}`}>
      <label className="margin-0 margin-bottom-5" htmlFor={name}>
        {label}
      </label>
      <div
        ref={wrapperRef}
        role="presentation"
        className="display-flex align-items-center gap-10 width-100pct"
        onChange={capturarFiltro}
        onScrollCapture={capturarRolagem}
        onClick={verificarAoAbrir}
      >
        {renderSelect()}
        {renderAcessorioDireita()}
      </div>
    </div>
  );
};

export type { InactiveSelectOption };
export default InputSelect;
